import React, { useState, useEffect } from 'react';
import { Bug, Code2, EyeOff, Minus, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import * as Sentry from '@sentry/react';
import { useDevMode } from '../context/DevModeContext';
import { functions } from '../services/appwrite';
import sentryConfig from '../config/sentry';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { cn } from '../lib/utils';

/**
 * DevMenu — floating, minimisable dev tools panel (bottom-right).
 *
 * Shown only while dev mode is enabled (sidebar user menu). It replaces the old
 * inline "DEV" badge: the launcher button itself signals that dev mode is on,
 * and glows amber while obfuscation is active.
 *
 * Options live in the expanded panel: global toggles first, then any actions
 * scoped to what the user currently has open (registered via useDevQuoteRequest).
 */

const STORAGE_EXPANDED = 'pg-dev-menu-expanded';

/** Long enough that a double-click cannot spend two of the month's error budget. */
const SENTRY_COOLDOWN_SECONDS = 5;

const DevMenu = () => {
  const { devMenuVisible, obfuscate, toggleObfuscate, quoteRequestContext } = useDevMode();
  const [resetting, setResetting] = useState(false);

  // Sentry smoke test
  const [sentryMessage, setSentryMessage] = useState('');
  const [sentrySending, setSentrySending] = useState(false);
  const [sentryCooldown, setSentryCooldown] = useState(0);
  const [lastEventId, setLastEventId] = useState(null);

  useEffect(() => {
    if (sentryCooldown <= 0) return undefined;
    const timer = setTimeout(() => setSentryCooldown((n) => n - 1), 1000);
    return () => clearTimeout(timer);
  }, [sentryCooldown]);

  const [expanded, setExpanded] = useState(() => {
    try {
      // Default to expanded the first time dev mode is turned on.
      return localStorage.getItem(STORAGE_EXPANDED) !== 'false';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_EXPANDED, expanded ? 'true' : 'false');
    } catch {
      // ignore storage failures
    }
  }, [expanded]);

  /**
   * Wipe every ML and AI result on the open quote request and detect from
   * scratch — part, damage AND angle detection, as though the photos had just
   * been uploaded. This is a testing tool: it throws away the angle model's
   * left/right calls, which is exactly what the user-facing "Update with AI"
   * in the damage section must never do.
   */
  const handleResetMl = async () => {
    const { quoteRequestId, organisationId } = quoteRequestContext || {};
    if (!quoteRequestId) return;
    if (!window.confirm(
      'Wipe ALL ML and AI results for this quote request (including photo angle detection) '
      + 'and re-detect from scratch?\n\nThe damage assessment will be deleted. '
      + 'To re-run just the assessment, use "Update with AI" in the Damage section instead.',
    )) return;

    try {
      setResetting(true);
      await functions.createExecution(
        'reassess-damage',
        JSON.stringify({ quoteRequestId, organisationId }),
        true, // async — reassess-damage is long-running
        '/',
        'POST',
      );
      toast.success('ML reset started — photos are being re-detected from scratch');
    } catch (err) {
      console.error('Failed to reset ML:', err);
      toast.error('Failed to reset ML: ' + err.message);
    } finally {
      setResetting(false);
    }
  };

  /**
   * Raise a real error and report it, to prove the pipeline end to end.
   *
   * The Error is constructed HERE, inside the bundle, on purpose: `beforeSend` in
   * config/sentry.js drops events whose every frame lacks an http(s) filename, so
   * an error thrown from the devtools console is discarded and looks exactly like
   * a broken integration. This one carries real frames.
   *
   * `flush` is what turns "sent" from a claim into an observation — it resolves
   * once the queue drains rather than when the event is handed over. It cannot
   * see a content blocker swallowing the request, which is the failure the
   * tunnel exists to remove; the hint below says so rather than implying a green
   * toast means Sentry has it.
   */
  const handleSentryTest = async () => {
    const text = sentryMessage.trim() || 'PanelGear Sentry test — raised deliberately';
    setSentrySending(true);
    try {
      const eventId = Sentry.captureException(new Error(text));
      setLastEventId(eventId || null);
      const drained = await Sentry.flush(5000);
      if (drained) {
        toast.success('Test error flushed to Sentry', {
          description: eventId ? `Event ${eventId}` : text,
        });
      } else {
        toast.warning('Still queued after 5s', {
          description: 'A content blocker or a slow network. Check the Network tab.',
        });
      }
    } catch (err) {
      toast.error('Could not send the test error', { description: err.message });
    } finally {
      setSentrySending(false);
      setSentryCooldown(SENTRY_COOLDOWN_SECONDS);
    }
  };

  if (!devMenuVisible) return null;

  // Minimised: just a launcher button. Glows amber when obfuscation is active.
  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        title="Open dev menu"
        aria-label="Open dev menu"
        className={cn(
          'fixed bottom-4 right-4 z-50 h-11 w-11 rounded-full border shadow-lg flex items-center justify-center transition-colors',
          obfuscate
            ? 'bg-amber-500 text-white border-amber-600 hover:bg-amber-500/90'
            : 'bg-card text-foreground hover:bg-accent',
        )}
      >
        <Code2 className="h-5 w-5" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 w-64 rounded-lg border bg-card text-card-foreground shadow-xl">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Code2 className="h-4 w-4" />
          <span>Dev Mode</span>
        </div>
        <button
          type="button"
          onClick={() => setExpanded(false)}
          title="Minimise"
          aria-label="Minimise dev menu"
          className="h-6 w-6 rounded flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        >
          <Minus className="h-4 w-4" />
        </button>
      </div>

      {/* Options */}
      <div className="p-2">
        <label
          htmlFor="dev-obfuscate"
          className="flex items-start justify-between gap-3 rounded-md p-2 hover:bg-accent cursor-pointer"
        >
          <div className="flex items-start gap-2 min-w-0">
            <EyeOff className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <p className="text-sm font-medium leading-tight">Obfuscate personal info</p>
              <p className="text-xs text-muted-foreground leading-tight mt-0.5">
                Hide names, contacts &amp; rego for screenshots
              </p>
            </div>
          </div>
          <Switch
            id="dev-obfuscate"
            checked={obfuscate}
            onCheckedChange={toggleObfuscate}
          />
        </label>

        {/* Sentry smoke test */}
        <div className="mt-1 pt-2 border-t">
          <p className="px-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Sentry
          </p>
          <div className="p-2 space-y-1.5">
            <Input
              value={sentryMessage}
              onChange={(e) => setSentryMessage(e.target.value)}
              placeholder="Test message (optional)"
              aria-label="Sentry test error message"
              maxLength={200}
              disabled={!sentryConfig.enabled}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !sentrySending && sentryCooldown === 0 && sentryConfig.enabled) {
                  handleSentryTest();
                }
              }}
            />
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start"
              onClick={handleSentryTest}
              disabled={!sentryConfig.enabled || sentrySending || sentryCooldown > 0}
            >
              <Bug className={cn('h-4 w-4 mr-2', sentrySending && 'animate-pulse')} />
              {!sentryConfig.enabled
                ? 'No DSN configured'
                : sentrySending
                  ? 'Sending…'
                  : sentryCooldown > 0
                    ? `Wait ${sentryCooldown}s`
                    : 'Send test error'}
            </Button>
            {lastEventId && (
              <p className="text-xs text-muted-foreground leading-tight">
                Last event <span className="font-mono break-all">{lastEventId}</span> — search that
                id in Sentry.
              </p>
            )}
            <p className="text-xs text-muted-foreground leading-tight">
              {sentryConfig.enabled
                ? `Raises a real error in ${sentryConfig.environment}. A content blocker can still swallow it silently.`
                : 'Set SENTRY_DSN_ADMIN and redeploy to enable this.'}
            </p>
          </div>
        </div>

        {/* Actions for whatever the user has open */}
        {quoteRequestContext?.quoteRequestId && (
          <div className="mt-1 pt-2 border-t">
            <p className="px-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              This quote request
            </p>
            <div className="p-2 space-y-1.5">
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={handleResetMl}
                disabled={resetting}
              >
                <RefreshCw className={cn('h-4 w-4 mr-2', resetting && 'animate-spin')} />
                Reset ML &amp; re-detect
              </Button>
              <p className="text-xs text-muted-foreground leading-tight">
                Wipes angle, part and damage detection plus the assessment, then starts over as if
                the photos were new.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DevMenu;
